<template>
	<el-row>
		<el-col :span="24">
			<div class="base-form">
				<h2 class="item" v-if="isCreatingViewMode">
					Новая группа сервисных работ
				</h2>
				<h2 class="item" v-else>
					Карточка группы <span>{{ serviceGroup?.name }}</span> сервисных работ
				</h2>
			</div>
		</el-col>		
	</el-row>
	<el-row>
		<el-col :span="24">
			<div class="base-form">
				<el-form :model="ruleForm" ref="jobForm" status-icon :rules="rules">
					<el-form-item label="Группа" label-position="top" prop="name">
						<el-input v-model="ruleForm.name" />
					</el-form-item>

					<el-form-item label="Описание" label-position="top" prop="description">
						<el-input v-model="ruleForm.description"
							maxlength="255"
							show-word-limit
							type="textarea" />
					</el-form-item>

					<el-form-item>
						<el-row style="width: 100%">
							<el-col :span="10">
								<el-button type="primary" :disabled="asyncOperationPending" @click="onFormSubmitClick">
									{{ isCreatingViewMode ? 'Создать' : 'Сохранить' }}
								</el-button>
								&nbsp;
								<el-button @click="onFormCancelClick">
									Отменить
								</el-button>
							</el-col>
							<el-col :span="14">
								<div v-if="!isCreatingViewMode" style="text-align: right;">
									<el-button type="danger" :disabled="asyncOperationPending" @click="onFormDeleteClick">
										Удалить группу
									</el-button>
								</div>
							</el-col>
						</el-row>
					</el-form-item>
				</el-form>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Opportunity } from '@element-plus/icons-vue';
import CatalogPickerComponent from '@/components/CatalogPickerComponent.vue';
import { ElButton, ElTable, ElLink, FormInstance, ElNotification, ElMessageBox } from 'element-plus';
import { reactive } from 'vue';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import EventHelper from '@/helpers/eventsHelper';
import ServiceJobGroupModel from '@/models/ServiceJobGroupModel';
import JobGroupsApiHelper from '@/helpers/api/JobGroupsApiHelper';
import { noun } from '@/helpers/formatters';

interface IServiceJobGroupFormFields {
	id: number;
	name: string;
	description: string | null;
}

@Options({
	components: {
		ElButton, ElTable, ElLink, CatalogPickerComponent, Opportunity
	},
	props: {
		params: Array
	}
})
export default class ServiceJobGroupView extends Vue {
	/** params */
	params!: Array<any>;
	isCreatingViewMode = false;

	/** constants */
	serviceGroup: ServiceJobGroupModel | null = null;
	
	/** view-models */
	asyncOperationPending = false;
	ruleForm = reactive<IServiceJobGroupFormFields>({
		id: 0,
		name: '',
		description: null
	});

	rules = reactive({
		name: [
			{ required: true, message: 'Название группы не может быть пустым'},
			{ min: 1, max: 255, message: 'Название группы не может быть короче 1 и длинее 255 символов'},			
		],
		description: [
			{ max: 255, message: 'Описание группы не может быть длинее 255 символов'},			
		]
	});
	
	mounted() {
		this.initForm();
	}

	/**
	 * init form with service-job-group's data from incoming params
	 */
	async initForm() {
		if (!this.params?.length) {
			alert('Wrong view arguments')
			return;
		}

		if (this.params[1] === true) {
			this.isCreatingViewMode = true;
			return;
		}

		if (!!this.params[0]?.id &&
			!!this.params[0]?.name) {
				this.serviceGroup = new ServiceJobGroupModel(this.params[0]);
				this.ruleForm.id = this.serviceGroup.id;
				this.ruleForm.name = this.serviceGroup.name;
				this.ruleForm.description = this.serviceGroup.description;				
				this.isCreatingViewMode = false;
				return;
		}

		alert('Wrong view arguments');
	}

	async onFormSubmitClick() {
		const form = this.$refs.jobForm as FormInstance;
		form.validate(async (valid) => {
			if (valid) {
				try {
					this.asyncOperationPending = true;
					if (this.isCreatingViewMode) {
						await this.addGroup();
					} else {
						await this.updateGroup();
					}
					ElNotification({
						message: `Группа работ успешно ${this.isCreatingViewMode ? 'создана' : 'обновлена'}`,
						type: 'success',
					});
					EventHelper.dispatchEvent('jobGroupsUpdate');
					this.closeCurrentTab();
				} catch (e: any) {
					if (e.code == 'ERR_NETWORK') {
						ElNotification({
							message: `Ошибка соединения, не удалось ${this.isCreatingViewMode ? 'создать' : 'обновить'} группу работ`,
							type: 'error',
						});
					} else {
						ElNotification({
							message: `Ошибка ${this.isCreatingViewMode ? 'создания новой' : 'обновления'} группы работ`,
							type: 'error',
						});
					}
					console.error('Error submitting service-job-group', e);
				}
				finally {
					this.asyncOperationPending = false;
				}
			}			
		});
	}

	onFormCancelClick() {
		this.closeCurrentTab();
	}

	/** deletes an existing service-job-group */
	async onFormDeleteClick(e: Event) {
		e.stopPropagation();

		const jobGroupsApiHelper = new JobGroupsApiHelper();
		const relatedJobIdsToGroup = await jobGroupsApiHelper.getRelatedJobIdsAsync(store.getters.jwt, this.ruleForm.id);

		let confirmationMessage: string;

		if (!relatedJobIdsToGroup ||
			relatedJobIdsToGroup?.length === 0) {
			confirmationMessage = `Группа '${this.serviceGroup!.name}' будет удалена`;
		} else {
			const conjunctedComponentNoun = noun.conjunct(relatedJobIdsToGroup!.length, 'сервисная работ', 'сервисных работы', 'сервисных работ');
			confirmationMessage = `В группе '${this.serviceGroup!.name}' есть ${relatedJobIdsToGroup?.length} ${conjunctedComponentNoun}. При удалении группы все компоненты будут перемещены в группу по-умолчанию`
		}

		ElMessageBox.confirm(
			confirmationMessage,
			{
				confirmButtonText: 'OK',
				cancelButtonText: 'Отмена',
				type: 'error',
			}
		)
		.then(() => this.deleteGroup(this.ruleForm.id));
	}

	closeCurrentTab() {		
		store.commit('removeTab', store.getters.tab);
	}

	async addGroup() {
		const request = {
			name: this.ruleForm.name,
			description: this.ruleForm.description
		};
		const jwt = store.getters.jwt;
		const a = axiosHelper.createInstance(jwt);
		await a.post('/api/servicejobgroups', request);
	}

	async updateGroup() {
		const request = {
			id: this.ruleForm.id,					
			name: this.ruleForm.name,
			description: this.ruleForm.description
		};
		const jwt = store.getters.jwt;
		const a = axiosHelper.createInstance(jwt);
		await a.put('/api/servicejobgroups', request);
	}

	async deleteGroup(groupId: number) {
		try {
			this.asyncOperationPending = true;
			const jwt = store.getters.jwt;
			const a = axiosHelper.createInstance(jwt);
			await a.delete(`/api/servicejobgroups/${groupId}`);
			ElNotification({
				title: 'Сервисная группа удалена',
				type: 'success',
			});
			EventHelper.dispatchEvent('jobCategoriesUpdate');
			this.closeCurrentTab();	
			this.asyncOperationPending = false;
		} catch (e) {
			ElNotification({
				title: 'Ошибка, сервисная группа не была удалена',
				type: 'error',
			});
			console.error(`Error deleting service-job-group #${groupId}`, e)
		} finally {
			this.asyncOperationPending = false;
		}
	}
}
</script>


<style scoped>
h2.item span {
	color: var(--el-color-primary);
}
</style>